import { useMemo } from 'react';
import SectionLabel from '../common/SectionLabel';
import { formatCurrency, formatPercent } from '../../utils/formatting';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function sumCat(monthData, category) {
  if (!monthData?.[category]) return 0;
  return Object.values(monthData[category]).reduce((s, v) => s + (v || 0), 0);
}

function computeTotals(data) {
  if (!data?.months) return null;
  let revenue = 0, cogs = 0, expenses = 0;
  for (const mk of MONTH_KEYS) {
    revenue += sumCat(data.months[mk], 'income');
    cogs += sumCat(data.months[mk], 'cogs');
    expenses += sumCat(data.months[mk], 'expenses');
  }
  return { revenue, cogs, expenses, grossProfit: revenue - cogs, netIncome: revenue - cogs - expenses };
}

const DIVISIONS = [
  { key: 'bwm', label: 'BWM', color: '#2596BE' },
  { key: 'bts', label: 'BTS', color: '#006D78' },
  { key: 'bia', label: 'BIA', color: '#3EAAD0' },
];

export default function DivisionHealth({ editableByClass, priorYearByClass }) {
  const globalTotals = useMemo(
    () => computeTotals(editableByClass?.global),
    [editableByClass]
  );

  const divisions = useMemo(() => {
    return DIVISIONS.map((div) => {
      const current = computeTotals(editableByClass?.[div.key]);
      const prior = computeTotals(priorYearByClass?.[div.key]);
      if (!current) return { ...div, current: null };

      const revenueShare = globalTotals?.revenue ? current.revenue / globalTotals.revenue : 0;
      const gpPct = current.revenue > 0 ? current.grossProfit / current.revenue : 0;
      const npPct = current.revenue > 0 ? current.netIncome / current.revenue : 0;
      const yoyGrowth = prior?.revenue ? (current.revenue - prior.revenue) / prior.revenue : null;

      return { ...div, current, revenueShare, gpPct, npPct, yoyGrowth };
    });
  }, [editableByClass, priorYearByClass, globalTotals]);

  if (!globalTotals) return null;

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Division Performance (2026)</SectionLabel>

      {/* Table-style layout - much tighter */}
      <table className="w-full text-[12px] mt-3">
        <thead>
          <tr className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#999]">
            <th className="text-left pb-2">Division</th>
            <th className="text-right pb-2">Revenue</th>
            <th className="text-right pb-2">Share</th>
            <th className="text-right pb-2">GP %</th>
            <th className="text-right pb-2">Net %</th>
            <th className="text-right pb-2">NOI</th>
            <th className="text-right pb-2">YoY</th>
          </tr>
        </thead>
        <tbody>
          {divisions.map((div) => {
            if (!div.current) return null;
            const isPositive = div.current.netIncome >= 0;
            return (
              <tr key={div.key} className="border-t border-gray-100">
                <td className="py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: div.color }} />
                    <span className="font-bold text-navy text-[13px]">{div.label}</span>
                  </div>
                </td>
                <td className="py-2.5 text-right font-bold text-navy">{formatCurrency(div.current.revenue)}</td>
                <td className="py-2.5 text-right text-[#999]">{formatPercent(div.revenueShare)}</td>
                <td className={`py-2.5 text-right font-bold ${div.gpPct >= 0.40 ? 'text-deep-teal' : 'text-rose'}`}>
                  {formatPercent(div.gpPct)}
                </td>
                <td className={`py-2.5 text-right font-bold ${isPositive ? 'text-deep-teal' : 'text-rose'}`}>
                  {formatPercent(div.npPct)}
                </td>
                <td className={`py-2.5 text-right font-bold ${isPositive ? 'text-deep-teal' : 'text-rose'}`}>
                  {formatCurrency(div.current.netIncome)}
                </td>
                <td className="py-2.5 text-right">
                  {div.yoyGrowth != null ? (
                    <span className={`font-bold ${div.yoyGrowth >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
                      {div.yoyGrowth >= 0 ? '+' : ''}{(div.yoyGrowth * 100).toFixed(1)}%
                    </span>
                  ) : '--'}
                </td>
              </tr>
            );
          })}

          {/* Global total row */}
          <tr className="border-t-2 border-navy/20">
            <td className="py-2.5">
              <span className="font-bold text-navy text-[13px]">Total</span>
            </td>
            <td className="py-2.5 text-right font-bold text-navy">{formatCurrency(globalTotals.revenue)}</td>
            <td className="py-2.5 text-right text-[#999]">100%</td>
            <td className={`py-2.5 text-right font-bold ${(globalTotals.grossProfit / globalTotals.revenue) >= 0.40 ? 'text-deep-teal' : 'text-rose'}`}>
              {formatPercent(globalTotals.grossProfit / globalTotals.revenue)}
            </td>
            <td className={`py-2.5 text-right font-bold ${globalTotals.netIncome >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
              {formatPercent(globalTotals.netIncome / globalTotals.revenue)}
            </td>
            <td className={`py-2.5 text-right font-bold ${globalTotals.netIncome >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
              {formatCurrency(globalTotals.netIncome)}
            </td>
            <td className="py-2.5"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
